"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.suggestionResultStatuses = exports.suggestionResultStatusMap = exports.StatsService = void 0;
var _moment = _interopRequireDefault(require("moment"));
var _models = require("../models");
var _stats = require("../utils/stats");
var _http = require("./http");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
class StatsService {
  static async getComputedRouteStats(period) {
    try {
      const data = await _http.HttpService.get('v1', '/stats/computed_itinerary', [...(0, _models.periodToParams)(period)]);
      const stats = parseComputedRouteStats(data);
      return stats;
    } catch (err) {
      console.error('[StatsService][getComputedRouteStats]', err);
      throw err;
    }
  }
  static async getOsmWaySections(partnerId) {
    try {
      const {
        bnac_data
      } = await _http.HttpService.get('v4', '/cyclability_zones/tags');
      const {
        geojson
      } = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/osm_way_sections`);
      const facilitiesMap = {};
      Object.keys(bnac_data).forEach(category => {
        bnac_data[category].forEach(facility => facilitiesMap[facility] = category);
      });
      return {
        ...geojson,
        features: geojson.features?.map(feature => ({
          ...feature,
          properties: {
            bnacFacilityLeft: feature.properties.facility_left || null,
            bnacFacilityRight: feature.properties.facility_right || null,
            closeFacility: facilitiesMap[feature.properties.close_facility || ''] || 'none',
            facilityLeft: facilitiesMap[feature.properties.facility_left || ''] || 'none',
            facilityRight: facilitiesMap[feature.properties.facility_right || ''] || 'none',
            wayName: feature.properties.way_name || null,
            roadType: feature.properties.road_type || null,
            osmWayId: feature.properties.osm_way_id || null,
            sectionLength: feature.properties.section_length || null
          }
        }), []) || []
      };
    } catch (err) {
      console.error('[StatsService][getOsmWaySections]', err);
      throw err;
    }
  }
  static async getSectionsSpeedsAndFrequencies(partnerId, period, {
    dayPeriod,
    timePeriod,
    departureZone,
    arrivalZone
  }) {
    try {
      const params = [{
        key: 'startDate',
        value: period.from.format('YYYY-MM-DD')
      }, {
        key: 'endDate',
        value: period.to.format('YYYY-MM-DD')
      }];
      if (dayPeriod) params.push({
        key: 'daysOfWeek',
        value: dayPeriod
      });
      if (timePeriod) params.push({
        key: 'timeframe',
        value: timePeriod.toLowerCase()
      });
      if (departureZone) params.push({
        key: 'startZoneId',
        value: departureZone.id
      });
      if (arrivalZone) params.push({
        key: 'endZoneId',
        value: arrivalZone.id
      });
      const res = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/sections_speeds_and_frequencies`, params);
      const stats = res.map(data => parseSectionStats(data));
      return stats;
    } catch (err) {
      console.error('[StatsService][getSectionsSpeedsAndFrequencies]', err);
      throw err;
    }
  }
  static async getSectionsCyclabilities(partnerId) {
    try {
      const res = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/sections_cyclability`);
      const stats = res.map(data => parseSectionStats(data));
      return stats;
    } catch (err) {
      console.error('[StatsService][getSectionsCyclabilities]', err);
      throw err;
    }
  }
  static async getSectionsQuality(partnerId, period) {
    try {
      const res = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/road_quality`, [{
        key: 'monthDate',
        value: period.from.format('YYYY-MM')
      }]);
      const stats = res.map(data => parseSectionStats(data));
      return stats;
    } catch (err) {
      console.error('[StatsService][getRoadsQuality]', err);
      throw new Error('');
    }
  }
  static async getSectionStats(partnerId, sectionId, period) {
    try {
      const params = [{
        key: 'period',
        value: 'custom'
      }, {
        key: 'date_start',
        value: period.from.format('DD-MM-YYYY')
      }, {
        key: 'date_end',
        value: period.to.format('DD-MM-YYYY')
      }];
      const {
        results
      } = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/section_passages/${sectionId}`, params);
      const stats = results.map(data => parseSectionStats(data));
      return stats;
    } catch (err) {
      console.error('[StatsService][getSectionStats]', err);
      throw new Error('');
    }
  }
  static async getTrafficExtrapolation(partnerId, period, dayPeriod, timePeriod) {
    try {
      const {
        traffic_extrapolation
      } = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/traffic_extrapolation_for_sections`, [{
        key: 'startDate',
        value: period.from.format('YYYY-MM-DD')
      }, {
        key: 'endDate',
        value: period.to.format('YYYY-MM-DD')
      }, {
        key: 'daysOfWeek',
        value: dayPeriod
      }, {
        key: 'timeframe',
        value: timePeriod.toLowerCase()
      }]);
      const stats = traffic_extrapolation.map(data => parseSectionStats(data));
      return stats;
    } catch (err) {
      console.error('[StatsService][getTrafficExtrapolation]', err);
      throw err;
    }
  }
  static async getDistancesTravelled(partnerId, period, dayPeriod = 'all', timePeriod = 'all_day') {
    try {
      const res = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/section_stats_distance_travelled`, [{
        key: 'startDate',
        value: period.from.format('YYYY-MM-DD')
      }, {
        key: 'endDate',
        value: period.to.format('YYYY-MM-DD')
      }, {
        key: 'daysOfWeek',
        value: dayPeriod
      }, {
        key: 'timeframe',
        value: timePeriod.toLowerCase()
      }]);
      return parseDistancesTravelled(res);
    } catch (err) {
      console.error('[StatsService][getDistancesTravelled]', err);
      throw new Error('');
    }
  }
  static async getTotalStopsDuration(partnerId, period, dayPeriod = 'all', timePeriod = 'all_day') {
    try {
      const res = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/total_stops_duration`, [{
        key: 'startDate',
        value: period.from.format('YYYY-MM-DD')
      }, {
        key: 'endDate',
        value: period.to.format('YYYY-MM-DD')
      }, {
        key: 'daysOfWeek',
        value: dayPeriod
      }, {
        key: 'timeframe',
        value: timePeriod.toLowerCase()
      }]);
      return res;
    } catch (err) {
      console.error('[StatsService][getTotalStopsDuration]', err);
      throw new Error('');
    }
  }
  static async getCyclabilityDistribution(partnerId) {
    try {
      const {
        '1': veryBad,
        '2': bad,
        '3': neutral,
        '4': good,
        '5': veryGood
      } = await _http.HttpService.get('v2', `/stats/partner/${partnerId}/cyclability_distribution`, []);
      return {
        veryGood,
        good,
        neutral,
        bad,
        veryBad
      };
    } catch (err) {
      console.error('[StatsService][getCyclabilityDistribution]', err);
      throw new Error('');
    }
  }
  static async getDiscontinuities({
    partnerId,
    quantity,
    facility_types
  }) {
    try {
      const params = [];
      if (quantity) params.push({
        key: 'quantity',
        value: quantity
      });
      if (facility_types && facility_types.length > 0) params.push(...facility_types.map(type => ({
        key: 'facility_types[]',
        value: type
      })));
      const res = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/discontinuity`, params);
      return res.map(value => parseDiscontinuity(value));
    } catch (err) {
      console.error('[StatsService][getDiscontinuities]', err);
      throw err;
    }
  }
  static async getDiscontinuityResolutions({
    id: partnerId
  }, id) {
    try {
      const res = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/discontinuity/${id}/resolution`);
      return res.map(value => parseDiscontinuityResolution(value));
    } catch (err) {
      console.error('[StatsService][getDiscontinuityResolutions]', err);
      throw err;
    }
  }
  static async getSuggestions({
    partnerId
  }) {
    try {
      const res = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/facility-suggestion`, []);
      return res.map(suggestion => parseSuggestion(suggestion));
    } catch (err) {
      console.error('[StatsService][getSuggestions]', err);
      throw new Error('');
    }
  }
  static async createSuggestion({
    partnerId,
    model,
    title,
    budget,
    startDate,
    endDate
  }) {
    try {
      const data = await _http.HttpService.post('v1', `/stats/partner/${partnerId}/facility-suggestion`, [], [], JSON.stringify({
        model,
        title,
        budget,
        start_date: startDate,
        end_date: endDate
      }));
      return parseSuggestion(data);
    } catch (err) {
      console.error('[StatsService][createSuggestion]', err);
      throw err;
    }
  }
  static async updateSuggestion({
    partnerId,
    suggestionId,
    model,
    title,
    budget,
    startDate,
    endDate
  }) {
    try {
      const props = {};
      if (model) props.model = model;
      if (title) props.title = title;
      if (budget !== undefined) props.budget = budget;
      if (startDate) props.start_date = startDate;
      if (endDate) props.end_date = endDate;
      const data = await _http.HttpService.patch('v1', `/stats/partner/${partnerId}/facility-suggestion/${suggestionId}`, [], [], JSON.stringify(props));
      return parseSuggestion(data);
    } catch (err) {
      console.error('[StatsService][updateSuggestion]', err);
      throw err;
    }
  }
  static async deleteSuggestion({
    partnerId,
    suggestionId
  }) {
    try {
      await _http.HttpService.delete('v1', `/stats/partner/${partnerId}/facility-suggestion/${suggestionId}`, [], []);
      return true;
    } catch (err) {
      console.error('[StatsService][deleteSuggestion]', err);
      throw err;
    }
  }
  static async getSuggestionResults({
    partnerId,
    suggestionId
  }) {
    try {
      const res = await _http.HttpService.get('v1', `/stats/partner/${partnerId}/facility-suggestion/${suggestionId}/result`, []);
      return res.map(({
        osm_way_section_id: osmWaySectionId,
        is_left_side_suggestion,
        is_confirmed
      }) => ({
        osmWaySectionId,
        isLeftSideSuggestion: is_left_side_suggestion,
        status: is_confirmed ? 'confirmed' : is_confirmed === false ? 'rejected' : 'unknown'
      }));
    } catch (err) {
      console.error('[StatsService][getSuggestionResults]', err);
      throw new Error('');
    }
  }
}
exports.StatsService = StatsService;
function parseComputedRouteStats({
  count,
  distance,
  data
}) {
  return new _models.ComputedRouteStats(count || 0, distance || 0, data?.map(({
    unit: strDate,
    ...data
  }) => {
    const date = (0, _stats.parseStatsDate)({
      strDate
    });
    if (!date) throw new Error('invalid stats');
    return {
      date,
      ...data
    };
  }) || []);
}
function parseSectionStats(data) {
  if ('frequency_backward' in data) {
    const {
      section_id,
      frequency_backward,
      frequency_onward,
      average_speed_backward,
      average_speed_onward
    } = data;
    return new _models.SectionStats(section_id, frequency_backward, frequency_onward, average_speed_backward, average_speed_onward);
  }
  if ('extrapolation' in data) {
    return new _models.SectionStats(data.section_id, undefined, undefined, undefined, undefined, undefined, undefined, Math.round(data.extrapolation));
  }
  if ('average_speed_going_onward_compared_to_osm_way_direction' in data) {
    return new _models.SectionStats(data.section_id, data.frequency_going_backward_compared_to_osm_way_direction, data.frequency_going_onward_compared_to_osm_way_direction, data.average_speed_going_backward_compared_to_osm_way_direction, data.average_speed_going_onward_compared_to_osm_way_direction, undefined, undefined, undefined, data.distance_travelled_going_backward_compared_to_osm_way_direction, data.distance_travelled_going_onward_compared_to_osm_way_direction, (0, _moment.default)(data.day_date));
  }
  if ('cyclability_left' in data) {
    const {
      id,
      cyclability_left,
      cyclability_right
    } = data;
    const cyclabilityMap = {
      1: 'veryBad',
      2: 'bad',
      3: 'neutral',
      4: 'good',
      5: 'veryGood'
    };
    return new _models.SectionStats(id, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, cyclability_right && cyclabilityMap[cyclability_right] || undefined, cyclability_left && cyclabilityMap[cyclability_left] || undefined);
  }
  const {
    section_id,
    normalized_backward_roughness,
    normalized_onward_roughness
  } = data;
  return new _models.SectionStats(section_id, 1, 1, undefined, undefined, normalized_backward_roughness.map(value => typeof value === 'number' ? Math.min(4, Math.max(0.01, 4 - value)) : null // force roughness to be inside ]0, 4]
  ), normalized_onward_roughness.map(value => typeof value === 'number' ? Math.min(4, Math.max(0.01, 4 - value)) : null));
}
const suggestionResultStatuses = exports.suggestionResultStatuses = ['confirmed', 'rejected', 'unknown'];
const suggestionResultStatusMap = exports.suggestionResultStatusMap = {
  confirmed: {
    color: '#165dff',
    label: 'Suggestion confirmée'
  },
  rejected: {
    color: '#e24949',
    label: 'Suggestion rejetée'
  },
  unknown: {
    color: '#f7ba1e',
    label: 'Suggestion en attente'
  }
};
function parseSuggestion({
  id,
  created,
  model,
  title,
  partner_id,
  budget,
  start_date,
  end_date,
  resolution_date
}) {
  return new _models.Suggestion(id, (0, _moment.default)(created), model, title, partner_id, budget, (0, _moment.default)(start_date), (0, _moment.default)(end_date), resolution_date ? (0, _moment.default)(resolution_date) : null);
}
function parseDiscontinuity({
  id,
  index,
  section_id,
  section_onward,
  cyclability,
  entering_type,
  contact_facility,
  nb_trace_total,
  nb_trace_discontinuity,
  resolution_section_ids,
  resolution_following_way_direction
}) {
  return new _models.Discontinuity(id, section_id, index, section_onward, cyclability === 'very_good' ? 'veryGood' : cyclability === 'good' ? 'good' : cyclability === 'bad' ? 'bad' : cyclability === 'very_bad' ? 'veryBad' : 'neutral', entering_type, contact_facility, nb_trace_total, nb_trace_discontinuity, resolution_section_ids.map((sectionId, index) => ({
    sectionId,
    isOnward: resolution_following_way_direction[index]
  })));
}
function parseDiscontinuityResolution({
  resolution_section_ids,
  resolution_following_way_direction,
  score,
  distance
}) {
  return new _models.DiscontinuityResolution(resolution_section_ids.map((id, index) => ({
    id,
    isOnward: resolution_following_way_direction[index]
  })), score, distance);
}
function parseDistancesTravelled({
  total,
  cycleway,
  greenway,
  lane,
  sharebusway,
  opposite,
  mixedfacilities,
  forbidden,
  other,
  total_extrapolated
}) {
  return {
    cycleway,
    greenway,
    lane,
    mixedfacilities,
    opposite,
    sharebusway,
    forbidden,
    other,
    all: total,
    allExtrapolated: total_extrapolated || 0,
    none: other + forbidden
  };
}